"use client";

import { Lock } from "lucide-react";
import { useUser } from "@/features/auth/hooks/useUser";
import { useAuthModal } from "@/features/auth/hooks/useAuthModal";
import { usePricingModal } from "@/features/auth/hooks/usePricingModal";
import { RANGE_OPTIONS, isRangeLocked, type RangeOption } from "./constants";
import { capture } from "@/lib/analytics/posthog";

type RangeSelectorProps = {
  selectedRangeKey: string;
  onRangeChange: (key: string) => void;
  onRefresh: () => void;
  isLoading: boolean;
  isMobile?: boolean;
  primaryColor?: string;
  rangeOptions?: RangeOption[];
  /**
   * Which chart this selector drives ("ccu", "followers", "topsellers", …).
   * Optional so the ten existing call-sites keep compiling; they report as
   * "unknown" until one is passed.
   */
  chart?: string;
};

export function RangeSelector({
  selectedRangeKey,
  onRangeChange,
  onRefresh,
  isLoading,
  isMobile = false,
  primaryColor = "#66c0f4",
  rangeOptions = RANGE_OPTIONS,
  chart = "unknown",
}: RangeSelectorProps) {
  const { plan } = useUser();
  const { open: openAuth } = useAuthModal();
  const { open: openPricing } = usePricingModal();

  const containerClasses = isMobile
    ? "flex gap-1.5 w-full overflow-x-auto overflow-y-hidden whitespace-nowrap pb-1 -mx-1 px-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    : "flex items-center gap-2";

  const buttonBase = isMobile
    ? "px-2 py-1 text-xs rounded border transition flex-shrink-0 min-w-max"
    : "px-3 py-1.5 text-sm rounded border transition flex-shrink-0 min-w-max";

  return (
    <div className={containerClasses}>
      {rangeOptions.map((option: RangeOption) => {
        const locked = isRangeLocked(option, plan);

        return (
          <button
            key={option.key}
            className={`${buttonBase} ${
              selectedRangeKey === option.key && !locked
                ? "text-[#0e1419]"
                : locked
                  ? // Solid #8f98a0 (≈6:1 vs #16202d) — half-opacity was 2.4
                    // and failed contrast. Lock icon + dashed border carry
                    // the "disabled" cue instead.
                    "border-dashed border-[#2a475e]/70 text-[#8f98a0] cursor-pointer"
                  : "border-[#2a475e] text-[#c7d5e0]"
            }`}
            style={
              selectedRangeKey === option.key && !locked
                ? {
                    backgroundColor: primaryColor,
                    borderColor: primaryColor,
                  }
                : {}
            }
            onMouseEnter={(e) => {
              if (selectedRangeKey !== option.key && !locked) {
                e.currentTarget.style.borderColor = primaryColor;
              }
            }}
            onMouseLeave={(e) => {
              if (selectedRangeKey !== option.key && !locked) {
                e.currentTarget.style.borderColor = "#2a475e";
              }
            }}
            onClick={() => {
              if (locked) {
                // The honest top of the upgrade funnel: someone wanted a
                // window their plan does not include. Without this event
                // "pricing modal opened" has no denominator.
                capture("paywall hit", {
                  feature: `chart_history:${chart}:${option.key}`,
                  plan,
                });
                if (plan === "anonymous") {
                  openAuth();
                } else {
                  openPricing("chart_range_lock");
                }
                return;
              }
              capture("chart range changed", { chart, range: option.key });
              onRangeChange(option.key);
            }}
          >
            <span className="inline-flex items-center gap-1">
              {option.label}
              {locked && <Lock className="w-3 h-3" />}
            </span>
          </button>
        );
      })}
      <button
        className={`${buttonBase} border-[#2a475e] text-[#c7d5e0] disabled:opacity-60`}
        style={{}}
        onMouseEnter={(e) => {
          if (!isLoading) {
            e.currentTarget.style.borderColor = primaryColor;
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = "#2a475e";
        }}
        onClick={onRefresh}
        disabled={isLoading}
      >
        Refresh
      </button>
    </div>
  );
}
